import React, { useState, useEffect } from 'react';
import { CommentInput, Comments, FilePreview } from 'kh-design-system/components';
import { addComment, getComments } from '../../revampCRUD/comments.crud';
import { multipleUploadImages } from '../../revampCRUD/uploads.crud';
import { successToaster, errorToaster } from '../../reusable/common';
import CommentItemShimmer from './CommentItemShimmer';
import { ErrMessages, CrudMessages } from '../../revampPages/EventInfo/config/error-constants';
import { validateFileCount, validateFile } from './validation';
import { KonfhubButton } from '../Atom/Buttons';
import { isImageFile } from '../../reusable/common';


const CommentsSection = ({ taskId, exhibitorId }) => {
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comments, setComments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [commentText, setCommentText] = useState('');

    const LIMIT = 30;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const fileUploadProps = {
        maxNofFiles: 5,
        maxFileSizeMB: 15,
        acceptedFileTypes: [
            'image/*',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ]
    };
    const loadComments = async () => {
        setLoading(true);
        try {
            const response = await getComments(exhibitorId, taskId, LIMIT, 0);
            const newComments = response.data;
            setComments(newComments);
            setOffset(newComments.length);
            setHasMore(newComments.length === LIMIT);
        } catch (err) {
            errorToaster(err.response?.data?.message || ErrMessages.DEFAULT_ERROR_MESSAGE);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreComments = async () => {
        if (!hasMore) return;
        setIsLoadingMore(true);
        try {
            const response = await getComments(exhibitorId, taskId, LIMIT, offset);
            const newComments = response.data;
            setComments(prev => [...prev, ...newComments]);
            setOffset(prev => prev + newComments.length);
            if (newComments.length < LIMIT) {
                setHasMore(false);
            }
        } catch (err) {
            errorToaster(err.response?.data?.message || ErrMessages.DEFAULT_ERROR_MESSAGE);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (exhibitorId && taskId) {
            setOffset(0);
            setHasMore(true);
            loadComments();
        }
    }, [exhibitorId, taskId]);

    useEffect(() => {
        setAttachments([]);
    }, [taskId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!commentText.trim()) {
            setError('Please enter a comment to submit');
            setIsSubmitting(false);
            return;
        }
        setError('');

        try {
            await addComment(exhibitorId, taskId, {
                comment: commentText,
                attachments
            });
            successToaster(CrudMessages.ADD_MESSAGE);
            setAttachments([]);
            setCommentText('');
            setOffset(0);
            setHasMore(true);
            loadComments();
        } catch (err) {
            errorToaster(err.response?.data?.message || ErrMessages.DEFAULT_ERROR_MESSAGE);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadFile = async (file, error) => {
        if (error) return errorToaster(error);

        try {
            setIsUploading(true);
            const files = Array.isArray(file) ? file : [file];

            const countError = validateFileCount(attachments.length, files.length, fileUploadProps.maxNofFiles);
            if (countError) {
                return errorToaster(countError);
            }

            const newAttachments = [];
            for (const singleFile of files) {
                const validationError = validateFile(singleFile, fileUploadProps.acceptedFileTypes, fileUploadProps.maxFileSizeMB);
                if (validationError) {
                    errorToaster(validationError);
                    continue;
                }

                const response = await multipleUploadImages(
                    singleFile,
                    19,
                    singleFile.type,
                );

                newAttachments.push({ url: response, file_name: singleFile.name, file_type: singleFile.type, file_size: singleFile.size });
            }

            setAttachments(prev => [...prev, ...newAttachments]);
            successToaster(`Files ${CrudMessages.UPLOAD_MESSAGE}`);
        } catch (error) {
            errorToaster(error.response?.data?.message || ErrMessages.DEFAULT_ERROR_MESSAGE);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteFile = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleCommentInputChange = (e) => {
        const text = e.target.value;
        setCommentText(text);
        if (text.trim() !== '') {
            setError('');
        }
    };

    return (
        <>
            <div className="comments-section">
                <CommentInput
                    value={commentText}
                    onChange={handleCommentInputChange}
                    onSubmit={handleSubmit}
                    uploadFile={handleUploadFile}
                    fileUploadProps={fileUploadProps}
                    disabled={isSubmitting}
                    sendElement={<img src="./assets/images/send.svg" />}
                    uploadElement={<img src="./assets/images/attach_file.svg" />}
                />
                {error && (
                    <div className="text-danger pt-1 mb-0">{error}</div>
                )}

                <div className='d-flex flex-wrap'>
                    {isUploading && (
                        <div className="image-dummy-container">
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                            />
                        </div>
                    )}
                    {attachments.map((file, index) => (
                        <FilePreview
                            key={file.url}
                            url={file.url}
                            onDelete={() => handleDeleteFile(index)}
                            containerStyle={{
                                width: '145px',
                                height: '100px',
                                cursor: 'pointer',
                                margin: '7px'
                            }}
                            deleteIconColor="red"
                            showDeleteButton={true}
                            isImage={isImageFile(file.url)}
                        />
                    ))}
                </div>
                {loading && !comments.length ? (
                    <CommentItemShimmer />
                ) : (
                    <>
                        <Comments comments={comments} />
                        <div className="py-3 button-container justify-content-center row w-100 m-0 d-flex">
                            {hasMore && (
                                <KonfhubButton
                                    className="mt-3 mb-3 mt-sm-0 button-text"
                                    onClick={loadMoreComments}
                                    btnType="secondary"
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? 'Loading...' : 'Load More'}
                                </KonfhubButton>
                            )}
                        </div>
                    </>
                )}
            </div>
            <style jsx>{`
        .image-dummy-container {
          width: 145px;
          height: 100px;
          margin: 7px;
          border: 1px solid #c4c4c4;
          border-radius: 2px;
          background-color: #f5f5f5;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .icon-color{
          color: #572148;
        }
        .text-secondary{
          box-shadow: 0px 0px 0px rgba(0, 0, 0, 0.25) !important;
        }
        .button-text{
          border: 1px solid #6D7780 !important;
          border-radius: 2px !important;
          color: #6D7780 !important;
          padding: 0.3rem 1rem !important;
        }
        .button-container{
          padding-bottom: 2rem;
        }
      `}</style>
        </>
    );
};

export default CommentsSection;
